import React, { useState } from "react";
import {
  Box, Button, Grid, IconButton, TextField, Typography, MenuItem,
  Divider, CircularProgress, alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import TvIcon from "@mui/icons-material/Tv";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import { useNavigate } from "react-router-dom";
import {
  useCreateDeviceMutation,
  useGetPlaylistGroupsQuery,
  useGetCompanyQuery,
} from "../../../Api/attoDeskApi";
import { IPlaylistGroup } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import SignageSection, {
  signageLabelSx, signageInputSx,
} from "../../../Components/ui/SignageSection";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin, selectCurrentUser } from "../../../Store/Auth/AuthSelector";

const DeviceCreate: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [createDevice, { isLoading }] = useCreateDeviceMutation();
  const { data: groupsData } = useGetPlaylistGroupsQuery();

  const groups: IPlaylistGroup[] = (Array.isArray(groupsData?.data)
    ? ((groupsData?.data as unknown) as IPlaylistGroup[])
    : []).filter(g => g.is_active);

  const [deviceName, setDeviceName] = useState("");
  const [deviceCode, setDeviceCode] = useState("");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [playlistGroupId, setPlaylistGroupId] = useState<number | "">("");
  const [companyId, setCompanyId] = useState<number | "">("");

  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const isValid =
    deviceName.trim().length > 0 && (!isSuperAdmin || companyId !== "");

  const handleSubmit = async () => {
    if (!isValid) {
      showErrorMessage(
        isSuperAdmin && companyId === ""
          ? "Please pick a company"
          : "Device name is required"
      );
      return;
    }
    try {
      const payload: any = {
        device_name: deviceName.trim(),
        device_code: deviceCode.trim() || null,
        location: location.trim() || null,
        playlist_group_id: playlistGroupId === "" ? null : Number(playlistGroupId),
      };
      if (isSuperAdmin) payload.company_id = Number(companyId);
      const res = await createDevice(payload).unwrap();
      if (res.status) {
        showMessage("Device created");
        navigate("/device/list");
      } else {
        showErrorMessage((res as any).message || "Failed to create device");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to create device");
    }
  };

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "calc(100vh - 100px)", mx: -3, mt: -3 }}>
      {/* ── Top Bar ── */}
      <Box sx={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)",
        px: 3, py: 0, position: "sticky", top: 0, zIndex: 10,
        boxShadow: "0 4px 30px rgba(0,0,0,0.15)",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <IconButton
            onClick={() => navigate("/device/list")} size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.15)", borderRadius: 2,
              width: 38, height: 38, backdropFilter: "blur(10px)",
              bgcolor: "rgba(255,255,255,0.05)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.12)", borderColor: "rgba(255,255,255,0.3)" },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 18, color: "#e2e8f0" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.1)" }} />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
                boxShadow: "0 2px 8px rgba(0,131,143,0.4)",
              }}>
                <TvIcon sx={{ fontSize: 16, color: "#fff" }} />
              </Box>
              <Typography fontSize={18} fontWeight={800} color="#fff" letterSpacing="-0.02em">
                New Device
              </Typography>
            </Box>
            <Typography fontSize={11} color="rgba(255,255,255,0.45)" mt={0.25} ml={5.5}>
              Fill in the details to register a new screen
            </Typography>
          </Box>
          <Button
            variant="outlined" size="small"
            onClick={() => navigate("/device/list")}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 600,
              borderColor: "rgba(255,255,255,0.15)", color: "#94a3b8", px: 2.5,
              "&:hover": {
                borderColor: "rgba(255,255,255,0.3)",
                bgcolor: "rgba(255,255,255,0.05)", color: "#e2e8f0",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained" disableElevation size="small"
            disabled={!isValid || isLoading} onClick={handleSubmit}
            startIcon={
              isLoading
                ? <CircularProgress size={14} sx={{ color: "#fff" }} />
                : <SaveIcon sx={{ fontSize: 16 }} />
            }
            sx={{
              borderRadius: 2.5, textTransform: "none", fontSize: 12, fontWeight: 700, px: 3,
              background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
              boxShadow: "0 2px 12px rgba(0,131,143,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #006064 0%, #00838f 100%)",
                boxShadow: "0 4px 20px rgba(0,131,143,0.5)",
              },
              "&.Mui-disabled": {
                background: "rgba(255,255,255,0.08)",
                color: "rgba(255,255,255,0.25)",
              },
            }}
          >
            {isLoading ? "Saving..." : "Save Device"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid item xs={12} lg={8}>
            <SignageSection
              icon={<InfoOutlinedIcon sx={{ fontSize: 18, color: "#3b82f6" }} />}
              title="Device Information"
              badge="Required"
              accentColor="#3b82f6"
              subtitle="Device name, location, and notes"
            >
              <Grid container spacing={2.5}>
                {isSuperAdmin && (
                  <Grid item xs={12}>
                    <Typography sx={signageLabelSx}>Company *</Typography>
                    <TextField
                      select fullWidth size="small"
                      value={companyId === "" ? "" : String(companyId)}
                      onChange={(e) =>
                        setCompanyId(e.target.value === "" ? "" : Number(e.target.value))
                      }
                      sx={signageInputSx}
                    >
                      <MenuItem value="">— Pick a company —</MenuItem>
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={String(c.id)}>
                          {c.companyName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                {!isSuperAdmin && currentUser?.company && (
                  <Grid item xs={12}>
                    <Typography sx={{ ...signageLabelSx, mb: 0.5 }}>Company</Typography>
                    <Typography sx={{ fontSize: 13, color: "#475569" }}>
                      {currentUser.company.name}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Device Name *</Typography>
                  <TextField
                    fullWidth placeholder="e.g. Lobby TV"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                    sx={{
                      ...signageInputSx,
                      "& .MuiOutlinedInput-root": {
                        ...signageInputSx["& .MuiOutlinedInput-root"],
                        fontSize: 15, fontWeight: 600,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Location</Typography>
                  <TextField
                    fullWidth size="small"
                    placeholder="e.g. Reception, Floor 2"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    sx={signageInputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Notes</Typography>
                  <TextField
                    fullWidth size="small" multiline rows={3}
                    placeholder="Optional notes about this screen…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    sx={signageInputSx}
                  />
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item xs={12} lg={4}>
            <SignageSection
              icon={<SettingsIcon sx={{ fontSize: 18, color: "#00838f" }} />}
              title="Device Details"
              accentColor="#00838f"
              subtitle="Code and playlist assignment"
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Device Code</Typography>
                  <TextField
                    fullWidth size="small"
                    placeholder="Auto (TV001, TV002…)"
                    value={deviceCode}
                    onChange={(e) => setDeviceCode(e.target.value)}
                    helperText="Leave blank to auto-generate"
                    sx={{
                      ...signageInputSx,
                      "& .MuiFormHelperText-root": {
                        fontSize: 10, color: "#94a3b8", ml: 0,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={signageLabelSx}>Status</Typography>
                  <Box sx={{
                    p: 2, borderRadius: 2.5,
                    bgcolor: alpha("#94a3b8", 0.06),
                    border: "1px solid #e2e8f0",
                    display: "flex", alignItems: "center", gap: 1,
                  }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: "50%",
                      bgcolor: "#94a3b8",
                    }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                      Awaiting first heartbeat
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 0.75, mb: 0.75,
                  }}>
                    <PlaylistPlayIcon sx={{ fontSize: 14, color: "#00838f" }} />
                    <Typography sx={{ ...signageLabelSx, mb: 0 }}>
                      Assigned Playlist Group
                    </Typography>
                  </Box>
                  <TextField
                    select fullWidth size="small"
                    value={playlistGroupId}
                    onChange={(e) =>
                      setPlaylistGroupId(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    sx={signageInputSx}
                  >
                    <MenuItem value="">No Playlist Group</MenuItem>
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name} · {g.playlists_count ?? 0} playlists · {g.playback_mode}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </SignageSection>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default DeviceCreate;
